/**
 * Private files on the cell build (move map M28) — the stand-in for supabase.storage.from(bucket).upload /
 * createSignedUrl / createSignedUrls. The API checks each path against its bucket's rule (the same rules Supabase
 * storage ran) and hands back a presigned S3 URL; the browser PUTs or GETs S3 directly. Results keep supabase-js's
 * shapes so callers change only which function they call.
 */
import { cellPost, type CellError } from './api';

/** storage.from(bucket).upload(path, body, { contentType }) → { data: { path }, error } */
export async function cellUpload(bucket: string, path: string, body: Blob, contentType?: string): Promise<{ data: { path: string } | null; error: CellError | null }> {
  const type = contentType || body.type || 'application/octet-stream';
  const { data, error } = await cellPost<{ signedUrl: string; headers: Record<string, string> }>('/v1/pro-hub/files/upload', { bucket, path, contentType: type });
  if (error || !data) return { data: null, error: error ?? { message: 'Upload refused', status: 0 } };
  try {
    const r = await fetch(data.signedUrl, { method: 'PUT', headers: data.headers, body });
    if (!r.ok) return { data: null, error: { message: `Upload failed (${r.status})`, status: r.status } };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message || 'Upload failed', status: 0 } };
  }
  return { data: { path }, error: null };
}

/** storage.from(bucket).createSignedUrls(paths, expiresIn) → { data: [{ path, signedUrl, error }], error } */
export async function cellSignMany(bucket: string, paths: string[], expiresIn: number) {
  // Signing is a read: allowed while viewing as a pro, as on Supabase (signing was never fenced).
  return cellPost<Array<{ path: string; signedUrl: string | null; error: string | null }>>('/v1/pro-hub/files/sign', { bucket, paths, expiresIn }, { readInViewAs: true });
}

/** storage.from(bucket).createSignedUrl(path, expiresIn) → { data: { signedUrl }, error } */
export async function cellSign(bucket: string, path: string, expiresIn: number): Promise<{ data: { signedUrl: string } | null; error: CellError | null }> {
  const { data, error } = await cellSignMany(bucket, [path], expiresIn);
  if (error) return { data: null, error };
  const one = data?.[0];
  if (!one?.signedUrl) return { data: null, error: { message: one?.error ?? 'Object not found', status: 404 } };
  return { data: { signedUrl: one.signedUrl }, error: null };
}
